import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';

import {
  authAPI,
  billingAPI,
  BillingPlan,
  formatBillingInterval,
  formatPriceCents,
  getUserErrorMessage,
  hasActiveSubscription,
  User,
} from '../api/api';

const Payment: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [plan, setPlan] = useState<BillingPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const checkoutResult = searchParams.get('checkout');

  const loadUser = useCallback(async () => {
    setError('');
    try {
      const profile = await authAPI.getMe();
      setUser(profile);
      return profile;
    } catch (err: unknown) {
      setError(
        getUserErrorMessage(
          err,
          'Could not load your account. Try signing in again.',
        ),
      );
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [profile, subscriptionPlan] = await Promise.all([
          authAPI.getMe(),
          billingAPI.getPlan(),
        ]);
        if (!cancelled) {
          setUser(profile);
          setPlan(subscriptionPlan);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(
            getUserErrorMessage(
              err,
              'Could not load subscription details. Confirm billing is configured on the server.',
            ),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (checkoutResult !== 'success') {
      return;
    }
    let attempts = 0;
    const poll = window.setInterval(async () => {
      attempts += 1;
      const profile = await loadUser();
      if (profile && hasActiveSubscription(profile)) {
        setNotice('Your subscription is active. Expanded Ask research limits are unlocked.');
        window.clearInterval(poll);
        searchParams.delete('checkout');
        setSearchParams(searchParams, { replace: true });
        return;
      }
      if (attempts >= 8) {
        setNotice(
          'Payment received. If access is not unlocked yet, wait a moment and refresh — Stripe may still be confirming.',
        );
        window.clearInterval(poll);
      }
    }, 2000);
    return () => window.clearInterval(poll);
  }, [checkoutResult, loadUser, searchParams, setSearchParams]);

  const isSubscriber = useMemo(() => hasActiveSubscription(user), [user]);

  const priceLabel = useMemo(() => {
    if (!plan) {
      return '$9 / month';
    }
    const amount = formatPriceCents(plan.amount_cents);
    const interval = formatBillingInterval(plan);
    return `${amount} / ${interval}`;
  }, [plan]);

  const handleSubscribe = async () => {
    setCheckoutLoading(true);
    setError('');
    try {
      const origin = window.location.origin;
      const { checkout_url } = await billingAPI.createCheckoutSession(
        `${origin}/payment?checkout=success`,
        `${origin}/payment?checkout=cancel`,
      );
      window.location.assign(checkout_url);
    } catch (err: unknown) {
      setError(
        getUserErrorMessage(
          err,
          'Could not start checkout. Confirm billing is configured on the server and try again.',
        ),
      );
      setCheckoutLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    setError('');
    try {
      const origin = window.location.origin;
      const { portal_url } = await billingAPI.createPortalSession(`${origin}/payment`);
      window.location.assign(portal_url);
    } catch (err: unknown) {
      setError(
        getUserErrorMessage(
          err,
          'Could not open the billing portal. Subscribe first or try again in a moment.',
        ),
      );
      setPortalLoading(false);
    }
  };

  if (loading && !user) {
    return (
      <div className="container" style={{ marginTop: '4rem' }}>
        <p>Loading subscription…</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="container"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
        style={{ textAlign: 'center', marginTop: '50px' }}
      >
        <h1
          style={{
            fontSize: '3rem',
            marginBottom: '20px',
            color: 'var(--neon-cyan)',
            textShadow: 'var(--glow-cyan)',
          }}
        >
          Subscription
        </h1>
        <p
          style={{
            fontSize: '1.2rem',
            color: 'var(--text-secondary)',
            maxWidth: '640px',
            margin: '0 auto',
          }}
        >
          Recurring membership billed through Stripe Checkout. Unlock expanded daily Ask research
          capacity on your account.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="card"
        style={{
          textAlign: 'center',
          marginTop: '40px',
          maxWidth: '640px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        {isSubscriber ? (
          <>
            <h2 style={{ color: 'var(--neon-lime)', marginBottom: '12px' }}>You are subscribed</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
              {user?.subscription_status === 'active' || user?.subscription_status === 'trialing'
                ? `Billing status: ${user.subscription_status}`
                : 'Member access is active on your account.'}
              {user?.current_period_end
                ? ` Renews ${new Date(user.current_period_end).toLocaleDateString()}.`
                : null}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/ask" className="btn btn-primary">
                Use Ask
              </Link>
              <motion.button
                type="button"
                className="btn btn-secondary"
                disabled={portalLoading}
                onClick={handleManageBilling}
                whileHover={{ scale: portalLoading ? 1 : 1.03 }}
                whileTap={{ scale: portalLoading ? 1 : 0.97 }}
              >
                {portalLoading ? 'Opening billing portal…' : 'Manage billing'}
              </motion.button>
            </div>
          </>
        ) : (
          <>
            <h2 style={{ color: 'var(--neon-magenta)', marginBottom: '12px' }}>
              WisemenResearch member
            </h2>
            <p
              style={{
                fontSize: '2rem',
                color: 'var(--neon-cyan)',
                marginBottom: '8px',
                fontWeight: 600,
              }}
            >
              {priceLabel}
            </p>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
              Billed monthly through Stripe. Cancel or update payment details anytime in the customer
              portal after your first checkout.
            </p>
            <ul
              style={{
                textAlign: 'left',
                color: 'var(--text-secondary)',
                margin: '0 auto 24px',
                maxWidth: '420px',
                lineHeight: 1.6,
              }}
            >
              <li>100× daily Ask question and token limits versus the free tier</li>
              <li>Secure card entry on Stripe-hosted checkout</li>
              <li>Manage payment method and cancellation in the billing portal</li>
            </ul>
            <motion.button
              type="button"
              className="btn btn-primary"
              style={{ fontSize: '1.1rem', padding: '15px 30px' }}
              disabled={checkoutLoading || !plan}
              onClick={handleSubscribe}
              whileHover={{ scale: checkoutLoading ? 1 : 1.03 }}
              whileTap={{ scale: checkoutLoading ? 1 : 0.97 }}
            >
              {checkoutLoading ? 'Redirecting to checkout…' : 'Subscribe now'}
            </motion.button>
          </>
        )}

        {checkoutResult === 'cancel' && (
          <p style={{ color: 'var(--text-secondary)', marginTop: '20px' }}>
            Checkout was canceled. You can subscribe whenever you are ready.
          </p>
        )}
        {notice && (
          <p style={{ color: 'var(--neon-cyan)', marginTop: '20px' }} role="status">
            {notice}
          </p>
        )}
        {error && (
          <div className="error" style={{ marginTop: '20px' }} role="alert">
            {error}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default Payment;
